import React, { useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import api from "../lib/apiClient";

export default function LogoStudio({ open, onOpenChange, plan, onSelect }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [options, setOptions] = useState([]);
  const started = useRef(false);

  const generate = async () => {
    setLoading(true);
    setOptions([]);
    try {
      const res = await api.post(`/plans/${plan.plan_id}/logo-options`);
      setOptions(res.data.options);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Logo generation failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !started.current) {
      started.current = true;
      generate();
    }
    if (!open) started.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = async (logo, idx) => {
    setSaving(idx);
    try {
      const res = await api.post(`/plans/${plan.plan_id}/logo`, { logo });
      onSelect(res.data.logo, res.data.brand_kit);
      toast.success(res.data.brand_kit ? "Logo saved — matching banner updated" : "Logo saved");
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not save logo");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="logo-studio-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-5 w-5 text-orange-400" /> AI Logo Studio</DialogTitle>
          <DialogDescription className="text-slate-400">Pick your favourite. We generate a few options for {plan.business_name}.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="grid grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="aspect-square rounded-xl bg-slate-800 animate-pulse grid place-items-center">
                <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {options.map((o, i) => (
              <button
                key={i}
                data-testid={`logo-option-${i}`}
                onClick={() => pick(o, i)}
                disabled={saving !== null}
                className="group relative aspect-square rounded-xl overflow-hidden border-2 border-slate-700 hover:border-orange-400 transition bg-white"
              >
                <img src={o} alt={`option ${i + 1}`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 grid place-items-center bg-black/50 opacity-0 group-hover:opacity-100 transition">
                  {saving === i ? <Loader2 className="h-6 w-6 animate-spin text-white" /> : <span className="flex items-center gap-1 text-white text-sm font-semibold"><Check className="h-4 w-4" /> Use this</span>}
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-2">
          <Button data-testid="logo-regenerate-button" variant="outline" onClick={generate} disabled={loading} className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100">
            <RefreshCw className="h-4 w-4 mr-1" /> Generate new options
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
